<script lang="ts">
	import { onDestroy, onMount } from "svelte";
	import { yyyy, zzzz } from "../../../store.svelte";
	import { toast } from "../../../lib/toast.svelte";
	import Fa from "svelte-fa";
	import { faTimes, faArrowLeft, faSortAlphaDown, faSortAlphaUp, faSortNumericDown, faSortNumericUp } from "@fortawesome/free-solid-svg-icons";

	let summaryData = $state(null);
	let overviewData = $state(null);
	let xfmrBalanceData = $state(null);
	let xfmrPhaseData = $state(null);
	let devicePhaseData = $state(null);
	let loadPhaseData = $state(null);
	let feedersData = $state(null);
	let fdPhaseData = $state(null);
	let fdXfmrBalanceData = $state(null);
	let fdXfmrPhaseData = $state(null);
	let fdDevicePhaseData = $state(null);
	let fdLoadPhaseData = $state(null);
	let tableEdgeData = $state(null);
	let tableNodeData = $state(null);

	let loadingSummary = $state(true);
	let loadingOverview = $state(true);
	let loadingXfmrBalance = $state(true);
	let loadingXfmrPhase = $state(true);
	let loadingDevicePhase = $state(true);
	let loadingLoadPhase = $state(true);
	let loadingFeeders = $state(false);
	let loadingFdPhase = $state(false);
	let loadingFdXfmrBalance = $state(false);
	let loadingFdXfmrPhase = $state(false);
	let loadingFdDevicePhase = $state(false);
	let loadingFdLoadPhase = $state(false);
	let loadingTableEdge = $state(false);
	let loadingTableNode = $state(false);

	let activeTab: string = $state("overview");

	let selectedTable: string | null = $state(null);
	let tieDevices = $state(null);
	let loadingTieDevices = $state(false);
	let tieFilter: string = $state("");
	let feederFilter: string = $state("");
	let feederSort: string = $state("length-desc");
	let selectedFeeder: string | null = $state(null);

	const url: string = yyyy.uri.api;

	// Derived stats for summary box
	// Uses overviewData which returns { data: { meas: { displayUnit, conversionFactor }, overview: { ... } } }
	let stats = $derived.by(() => {
		if (!summaryData?.data?.overview) return null;
		const src = summaryData.data;
		const o = src.overview;
		const factor = src.meas?.conversionFactor || 1;
		const unit = src.meas?.displayUnit || "m";

		const priLength = o.priLength || 0;
		const secLength = o.secLength || 0;
		const totalLengthRaw = priLength + secLength;

		const feederCount = o.SOURCE || 0;
		const statusDeviceCount = o.DEVICE || 0;
		const transformerCount = o.TRANSFORMER || 0;
		const loadCount = o.LOAD || 0;
		const deviceCount = statusDeviceCount + transformerCount + loadCount;

		return {
			feederCount,
			totalLength: (totalLengthRaw * factor).toFixed(1),
			primaryLength: (priLength * factor).toFixed(1),
			secondaryLength: (secLength * factor).toFixed(1),
			primaryPct: o.priPercent || 0,
			secondaryPct: o.secPercent || 0,
			lineEnergizedPct: o.energized || 0,
			unit,
			factor,
			deviceCount,
			statusDeviceCount,
			transformerCount,
			loadCount,
			deviceEnergizedPct: o.energizedPercent || 0,
		};
	});

	// Derived stats from phase-breakdown response (overview tab)
	let overviewStats = $derived.by(() => {
		if (!overviewData?.data?.phaseBreakdown) return null;
		const src = overviewData.data;
		const pb = src.phaseBreakdown;
		const factor = src.meas?.conversionFactor || 1;
		const unit = src.meas?.displayUnit || "m";

		const phaseOrder = ["ABC", "AB", "AC", "BC", "A", "B", "C"];
		const phases = phaseOrder
			.filter(p => pb[p])
			.map(p => {
				const pri = pb[p]?.["1"] || 0;
				const sec = pb[p]?.["2"] || 0;
				const total = pri + sec;
				return {
					phase: p,
					primary: (pri * factor).toFixed(1),
					secondary: (sec * factor).toFixed(1),
					total: (total * factor).toFixed(1),
					primaryPct: total > 0 ? Math.round((pri / total) * 100) : 0,
					secondaryPct: total > 0 ? Math.round((sec / total) * 100) : 0,
				};
			});

		return { unit, phases };
	});

	// Derived stats from transformer-voltamps-balance response
	let xfmrBalance = $derived.by(() => {
		if (!xfmrBalanceData?.data?.xfmrVoltampBreakdown) return null;
		const v = xfmrBalanceData.data.xfmrVoltampBreakdown;
		const totalVa = v.voltamps_a || 0;
		const totalVb = v.voltamps_b || 0;
		const totalVc = v.voltamps_c || 0;
		const totalVabc = totalVa + totalVb + totalVc;

		return {
			totalVabc,
			vaMva: (totalVa / 1_000_000).toFixed(1),
			vbMva: (totalVb / 1_000_000).toFixed(1),
			vcMva: (totalVc / 1_000_000).toFixed(1),
			vaPct: totalVabc > 0 ? Math.round((totalVa / totalVabc) * 100) : 0,
			vbPct: totalVabc > 0 ? Math.round((totalVb / totalVabc) * 100) : 0,
			vcPct: totalVabc > 0 ? Math.round((totalVc / totalVabc) * 100) : 0,
		};
	});

	// Derived stats from transformer-phase-count response
	let xfmrByPhase = $derived.by(() => {
		if (!xfmrPhaseData?.data?.xfmrPhaseCount) return null;
		return parsePhaseCount(xfmrPhaseData.data.xfmrPhaseCount);
	});

	// Derived stats from device-phase-count response
	let devicesByPhase = $derived.by(() => {
		if (!devicePhaseData?.data?.devicePhaseCount) return null;
		return parsePhaseCount(devicePhaseData.data.devicePhaseCount);
	});

	// Derived stats from load-phase-count response
	let loadsByPhase = $derived.by(() => {
		if (!loadPhaseData?.data?.loadPhaseCount) return null;
		return parsePhaseCount(loadPhaseData.data.loadPhaseCount);
	});

	// Derived stats from table-breakdown-edge response (CUBE)
	let tableEdgeStats = $derived.by(() => {
		if (!tableEdgeData?.data?.tableListEdge) return null;
		const src = tableEdgeData.data;
		const factor = src.meas?.conversionFactor || 1;
		const unit = src.meas?.displayUnit || "m";
		const rows = src.tableListEdge;

		const tables = rows
			.filter(d => d.grouping === 1)
			.map(d => ({ table: d.lw_table, length: (d.length || 0) * factor }))
			.sort((a, b) => b.length - a.length);

		const phaseBreakdown = {};
		rows.filter(d => d.grouping === 0 && d.lw_table && d.phase).forEach(d => {
			if (!phaseBreakdown[d.lw_table]) phaseBreakdown[d.lw_table] = [];
			phaseBreakdown[d.lw_table].push({ phase: d.phase, length: (d.length || 0) * factor });
		});
		Object.values(phaseBreakdown).forEach(arr =>
			arr.sort((a, b) => (phaseOrder.indexOf(a.phase) ?? 99) - (phaseOrder.indexOf(b.phase) ?? 99))
		);

		const total = (rows.find(d => d.grouping === 3)?.length || 0) * factor;

		return { unit, tables, phaseBreakdown, total };
	});

	// Derived stats from table-breakdown-node response (CUBE)
	let tableNodeStats = $derived.by(() => {
		if (!tableNodeData?.data?.tableListNode) return null;
		const rows = tableNodeData.data.tableListNode;

		// grouping=1: per-table totals
		const tables = rows
			.filter(d => d.grouping === 1)
			.map(d => ({ table: d.lw_table, count: d.count || 0 }))
			.sort((a, b) => b.count - a.count);

		// grouping=0: per-table per-phase breakdown
		const phaseBreakdown = {};
		rows.filter(d => d.grouping === 0 && d.lw_table && d.phase).forEach(d => {
			if (!phaseBreakdown[d.lw_table]) phaseBreakdown[d.lw_table] = [];
			phaseBreakdown[d.lw_table].push({ phase: d.phase, count: d.count || 0 });
		});
		Object.values(phaseBreakdown).forEach(arr =>
			arr.sort((a, b) => (phaseOrder.indexOf(a.phase) ?? 99) - (phaseOrder.indexOf(b.phase) ?? 99))
		);

		// grouping=3: grand total
		const total = rows.find(d => d.grouping === 3)?.count || 0;

		return { tables, phaseBreakdown, total };
	});

	// Derived feeder list for Feeders tab
	let feederList = $derived.by(() => {
		if (!feedersData?.data?.feederList) return [];
		const src = feedersData.data;
		const factor = src.meas?.conversionFactor || 1;
		const unit = src.meas?.displayUnit || "m";

		const list = Object.entries(src.feederList)
			.map(([name, tiers]) => {
				const primary = tiers["1"] || 0;
				const secondary = tiers["2"] || 0;
				const total = primary + secondary;
				return {
					name,
					total,
					primary,
					secondary,
					totalDisplay: (total * factor).toFixed(1),
					primaryDisplay: (primary * factor).toFixed(1),
					secondaryDisplay: (secondary * factor).toFixed(1),
					primaryPct: total > 0 ? Math.round((primary / total) * 100) : 0,
					secondaryPct: total > 0 ? Math.round((secondary / total) * 100) : 0,
				};
			})
			.sort((a, b) => b.total - a.total);
		list.maxTotal = Math.max(0, ...list.map(f => f.total));
		list.unit = unit;
		return list;
	});

	// Per-feeder detail derived blocks (same shape as overview, sourced from fd* data)
	let fdPhaseBreakdown = $derived.by(() => {
		if (!fdPhaseData?.data?.phaseBreakdown) return null;
		const src = fdPhaseData.data;
		const factor = src.meas?.conversionFactor || 1;
		const unit = src.meas?.displayUnit || "m";
		const pb = src.phaseBreakdown;
		const phaseOrder = ['ABC', 'AB', 'AC', 'BC', 'A', 'B', 'C'];
		const phases = phaseOrder
			.filter(p => pb[p])
			.map(p => {
				const pri = pb[p]?.["1"] || 0;
				const sec = pb[p]?.["2"] || 0;
				const total = pri + sec;
				return {
					phase: p,
					primary: (pri * factor).toFixed(1),
					secondary: (sec * factor).toFixed(1),
					total: (total * factor).toFixed(1),
					primaryPct: total > 0 ? Math.round((pri / total) * 100) : 0,
					secondaryPct: total > 0 ? Math.round((sec / total) * 100) : 0,
				};
			});
		return { unit, phases };
	});

	let fdXfmrBalance = $derived.by(() => {
		if (!fdXfmrBalanceData?.data?.xfmrVoltampBreakdown) return null;
		const v = fdXfmrBalanceData.data.xfmrVoltampBreakdown;
		const totalVa = v.voltamps_a || 0;
		const totalVb = v.voltamps_b || 0;
		const totalVc = v.voltamps_c || 0;
		const totalVabc = totalVa + totalVb + totalVc;
		return {
			totalVabc,
			vaMva: (totalVa / 1_000_000).toFixed(1),
			vbMva: (totalVb / 1_000_000).toFixed(1),
			vcMva: (totalVc / 1_000_000).toFixed(1),
			vaPct: totalVabc > 0 ? Math.round((totalVa / totalVabc) * 100) : 0,
			vbPct: totalVabc > 0 ? Math.round((totalVb / totalVabc) * 100) : 0,
			vcPct: totalVabc > 0 ? Math.round((totalVc / totalVabc) * 100) : 0,
		};
	});

	const phaseOrder = ['ABC', 'AB', 'AC', 'BC', 'A', 'B', 'C'];
	const phaseColors = {
		A: 'rgb(152, 32, 19)', B: 'rgb(25, 149, 12)', C: 'rgb(24, 51, 139)',
		AB: '#8b4513', AC: '#5b2c6f', BC: '#1a5276', ABC: '#555',
	};

	let parsePhaseCount = (pc) => {
		let total = 0;
		const items = phaseOrder
			.filter(p => pc[p])
			.map(p => {
				const count = pc[p] || 0;
				total += count;
				return { phase: p, count, color: phaseColors[p] || '#999' };
			});
		return { items, total };
	};

	let fdXfmrByPhase = $derived.by(() => {
		if (!fdXfmrPhaseData?.data?.xfmrPhaseCount) return null;
		return parsePhaseCount(fdXfmrPhaseData.data.xfmrPhaseCount);
	});

	let fdDevicesByPhase = $derived.by(() => {
		if (!fdDevicePhaseData?.data?.devicePhaseCount) return null;
		return parsePhaseCount(fdDevicePhaseData.data.devicePhaseCount);
	});

	let fdLoadsByPhase = $derived.by(() => {
		if (!fdLoadPhaseData?.data?.loadPhaseCount) return null;
		return parsePhaseCount(fdLoadPhaseData.data.loadPhaseCount);
	});

	let fetchSection = async (rqst: string, extra: Record<string, any> = {}) => {
		const response = await fetch(`${url}/rpc/networkdata`, {
			method: "post",
			headers: {
				"Content-Type": "application/json",
				Authorization: "Bearer " + yyyy.sesh.logtoken.token,
			},
			body: JSON.stringify({
				livewire: yyyy.sesh.activeLivewire.wireident,
				rqst,
				...extra,
			}),
		});
		if (!response.ok) throw new Error(`Failed to load ${rqst}`);
		return await response.json();
	};

	let loadSummary = async () => {
		loadingSummary = true;
		try { summaryData = await fetchSection('summary'); }
		catch { toast.error("Failed to load summary", 3000, "top-center", "vertical"); }
		finally { loadingSummary = false; }
	};

	let loadOverview = async () => {
		loadingOverview = true;
		try { overviewData = await fetchSection('phase-breakdown'); }
		catch { toast.error("Failed to load overview", 3000, "top-center", "vertical"); }
		finally { loadingOverview = false; }
	};

	let loadXfmrBalance = async () => {
		loadingXfmrBalance = true;
		try { xfmrBalanceData = await fetchSection('transformer-voltamps-balance'); }
		catch { toast.error("Failed to load transformer balance", 3000, "top-center", "vertical"); }
		finally { loadingXfmrBalance = false; }
	};

	let loadXfmrPhase = async () => {
		loadingXfmrPhase = true;
		try { xfmrPhaseData = await fetchSection('transformer-phase-count'); }
		catch { toast.error("Failed to load transformer phase counts", 3000, "top-center", "vertical"); }
		finally { loadingXfmrPhase = false; }
	};

	let loadDevicePhase = async () => {
		loadingDevicePhase = true;
		try { devicePhaseData = await fetchSection('device-phase-count'); }
		catch { toast.error("Failed to load device phase counts", 3000, "top-center", "vertical"); }
		finally { loadingDevicePhase = false; }
	};

	let loadLoadPhase = async () => {
		loadingLoadPhase = true;
		try { loadPhaseData = await fetchSection('load-phase-count'); }
		catch { toast.error("Failed to load load phase counts", 3000, "top-center", "vertical"); }
		finally { loadingLoadPhase = false; }
	};

	let loadFeeders = async () => {
		loadingFeeders = true;
		try { feedersData = await fetchSection('feeder-list'); }
		catch { toast.error("Failed to load feeders", 3000, "top-center", "vertical"); }
		finally { loadingFeeders = false; }
	};

	let clearFeederDetail = () => {
		fdPhaseData = null;
		fdXfmrBalanceData = null;
		fdXfmrPhaseData = null;
		fdDevicePhaseData = null;
		fdLoadPhaseData = null;
	};

	let loadFeederDetail = (feeder: string) => {
		clearFeederDetail();
		loadingFdPhase = true;
		loadingFdXfmrBalance = true;
		loadingFdXfmrPhase = true;
		loadingFdDevicePhase = true;
		loadingFdLoadPhase = true;
		const extra = { feeder };

		fetchSection('phase-breakdown', extra)
			.then(d => fdPhaseData = d)
			.catch(() => toast.error("Failed to load feeder phase breakdown", 3000, "top-center", "vertical"))
			.finally(() => loadingFdPhase = false);

		fetchSection('transformer-voltamps-balance', extra)
			.then(d => fdXfmrBalanceData = d)
			.catch(() => toast.error("Failed to load feeder transformer balance", 3000, "top-center", "vertical"))
			.finally(() => loadingFdXfmrBalance = false);

		fetchSection('transformer-phase-count', extra)
			.then(d => fdXfmrPhaseData = d)
			.catch(() => toast.error("Failed to load feeder transformer phases", 3000, "top-center", "vertical"))
			.finally(() => loadingFdXfmrPhase = false);

		fetchSection('device-phase-count', extra)
			.then(d => fdDevicePhaseData = d)
			.catch(() => toast.error("Failed to load feeder device phases", 3000, "top-center", "vertical"))
			.finally(() => loadingFdDevicePhase = false);

		fetchSection('load-phase-count', extra)
			.then(d => fdLoadPhaseData = d)
			.catch(() => toast.error("Failed to load feeder load phases", 3000, "top-center", "vertical"))
			.finally(() => loadingFdLoadPhase = false);
	};

	let loadTableEdge = async () => {
		loadingTableEdge = true;
		try { tableEdgeData = await fetchSection('table-breakdown-edge'); }
		catch { toast.error("Failed to load table line data", 3000, "top-center", "vertical"); }
		finally { loadingTableEdge = false; }
	};

	let loadTableNode = async () => {
		loadingTableNode = true;
		try { tableNodeData = await fetchSection('table-breakdown-node'); }
		catch { toast.error("Failed to load table device data", 3000, "top-center", "vertical"); }
		finally { loadingTableNode = false; }
	};

	let fetchTieDevices = async () => {
		loadingTieDevices = true;
		let _hdrs = {
			"Content-Type": "application/json",
			Authorization: "Bearer " + yyyy.sesh.logtoken.token,
		};
		let _body = JSON.stringify({
			livewire: yyyy.sesh.activeLivewire.wireident,
		});
		let fetchopts = { method: "post", body: _body, headers: _hdrs };

		const response = await fetch(`${url}/rpc/show_tie_devices`, fetchopts);
		if (response.ok) {
			let resp = await response.json();
			tieDevices = resp.data;
			loadingTieDevices = false;
		} else {
			loadingTieDevices = false;
			toast.error("Failed to fetch tie devices", 3000, "top-center", "vertical");
		}
	};

	$effect(() => {
		if (activeTab === 'tie-devices') {
			if (tieDevices?.featureCollection) {
				zzzz.emit("showTieDevicesOnMap", tieDevices.featureCollection);
			}
		} else {
			zzzz.emit("hideTieDevices");
		}
	});

	onMount(() => {
		zzzz.emit("NetworkInfo");
		loadSummary();
		loadOverview();
		loadXfmrBalance();
		loadXfmrPhase();
		loadDevicePhase();
		loadLoadPhase();
	});

	onDestroy(() => {
		zzzz.emit("hideTieDevices");
		zzzz.emit("end Network Info");
	});
</script>

<div id="network-info">
		<!-- Pinned: header + summary + tabs -->
		<div class="pinned-top">
		<div class="panel-header">
			<button class="close-btn" onclick={() => {
				yyyy.ctrl.control = undefined;
				yyyy.ctrl.expanded = false;
			}}>
				<Fa icon={faTimes} size="1.75x" />
			</button>
			<span class="panel-title">{yyyy.sesh.activeLivewire.livewire} Statistics</span>
		</div>
		<div class="summary-box">
			{#if stats}
			<div class="feeder-count-box">
				<span class="big-number">{stats.feederCount}</span>
				<span class="big-label">Feeders</span>
			</div>
			<div class="summary-stats">
				<div class="stat-row">
					<div class="stat">
						<span class="stat-heading">Total Length</span>
						<span class="stat-value">{stats.totalLength} {stats.unit}</span>
					</div>
					<div class="stat">
						<span class="stat-heading">Primary</span>
						<span class="stat-value color-primary">{stats.primaryLength} {stats.unit}</span>
						<span class="stat-pct">{stats.primaryPct}%</span>
					</div>
					<div class="stat">
						<span class="stat-heading">Secondary</span>
						<span class="stat-value color-secondary">{stats.secondaryLength} {stats.unit}</span>
						<span class="stat-pct">{stats.secondaryPct}%</span>
					</div>
					<div class="stat">
						<span class="stat-heading">Energized</span>
						<span class="stat-value">{stats.lineEnergizedPct}%</span>
					</div>
				</div>
				<div class="stat-divider"></div>
				<div class="stat-row">
					<div class="stat">
						<span class="stat-heading">Devices</span>
						<span class="stat-value">{stats.statusDeviceCount}</span>
					</div>
					<div class="stat">
						<span class="stat-heading">Transformers</span>
						<span class="stat-value">{stats.transformerCount}</span>
					</div>
					<div class="stat">
						<span class="stat-heading">Loads</span>
						<span class="stat-value">{stats.loadCount}</span>
					</div>
					<div class="stat">
						<span class="stat-heading">Energized</span>
						<span class="stat-value">{stats.deviceEnergizedPct}%</span>
						<span class="stat-pct">{stats.deviceCount} total</span>
					</div>
				</div>
			</div>
			{:else}
			<div class="section-spinner-row">
				<div class="spinner-sm"></div>
			</div>
			{/if}
		</div>

		<!-- Tabs -->
		<div class="tab-bar">
			<button class="tab" class:active={activeTab === 'overview'} onclick={() => activeTab = 'overview'}>Overview</button>
			<button class="tab" class:active={activeTab === 'feeders'} onclick={() => {
				activeTab = 'feeders';
				if (!feedersData && !loadingFeeders) loadFeeders();
			}}>Feeders</button>
			<button class="tab" class:active={activeTab === 'tables'} onclick={() => {
				activeTab = 'tables';
				if (!tableEdgeData && !loadingTableEdge) loadTableEdge();
				if (!tableNodeData && !loadingTableNode) loadTableNode();
			}}>Tables</button>
			<button class="tab" class:active={activeTab === 'tie-devices'} onclick={() => {
				activeTab = 'tie-devices';
				if (!tieDevices) fetchTieDevices();
			}}>Tie Devices</button>
		</div>

		</div>
		<!-- Tab Content -->
		<div class="tab-content">
			{#if activeTab === 'overview'}
				<!-- Phase Breakdown -->
				{#if overviewStats?.phases?.length > 0}
					<div class="section">
						<h5 class="section-title">Phase Breakdown</h5>
						{#each overviewStats.phases as ph}
							<div class="phase-split-row">
								<span class="device-phase phase-{ph.phase.toLowerCase()}">{ph.phase}</span>
								<div class="phase-split-bar-area">
									<div class="split-bar phase-split-bar">
										<div class="split-segment split-primary" style="width: {ph.primaryPct}%"></div>
										<div class="split-segment split-secondary" style="width: {ph.secondaryPct}%"></div>
										<span class="split-bar-total">{ph.total} {overviewStats.unit}</span>
									</div>
									<div class="phase-split-values">
										<span class="phase-split-val"><span class="split-label-primary">Primary</span><span class="split-dot-inline split-dot-primary"></span>{ph.primary} {overviewStats.unit} ({ph.primaryPct}%)</span>
										<span class="phase-split-val"><span class="split-label-secondary">Secondary</span><span class="split-dot-inline split-dot-secondary"></span>{ph.secondary} {overviewStats.unit} ({ph.secondaryPct}%)</span>
									</div>
								</div>
							</div>
						{/each}
					</div>
				{:else}
					<div class="section section-loading">
						<h5 class="section-title">Phase Breakdown</h5>
						<div class="section-spinner-row"><div class="spinner-sm"></div></div>
					</div>
				{/if}

				<!-- Transformer Balance -->
				{#if xfmrBalance?.totalVabc > 0}
					<div class="section">
						<h5 class="section-title">Transformer Balance</h5>
						<div class="balance-bar">
							<div class="balance-segment seg-a" style="width: {xfmrBalance.vaPct}%"></div>
							<div class="balance-segment seg-b" style="width: {xfmrBalance.vbPct}%"></div>
							<div class="balance-segment seg-c" style="width: {xfmrBalance.vcPct}%"></div>
						</div>
						<div class="balance-legend">
							<span class="balance-item"><span class="balance-dot seg-a-bg"></span>A: {xfmrBalance.vaMva} MVA ({xfmrBalance.vaPct}%)</span>
							<span class="balance-item"><span class="balance-dot seg-b-bg"></span>B: {xfmrBalance.vbMva} MVA ({xfmrBalance.vbPct}%)</span>
							<span class="balance-item"><span class="balance-dot seg-c-bg"></span>C: {xfmrBalance.vcMva} MVA ({xfmrBalance.vcPct}%)</span>
						</div>
					</div>
				{:else}
					<div class="section section-loading">
						<h5 class="section-title">Transformer Balance</h5>
						<div class="section-spinner-row"><div class="spinner-sm"></div></div>
					</div>
				{/if}

				<!-- Transformers by Phase -->
				{#if xfmrByPhase?.items?.length > 0}
					<div class="section">
						<h5 class="section-title">Transformers by Phase ({xfmrByPhase.total})</h5>
						<div class="split-bar" style="height: 28px;">
							{#each xfmrByPhase.items as slice}
								<div class="split-segment split-segment-label phase-seg-{slice.phase.toLowerCase()}" style="width: {xfmrByPhase.total > 0 ? (slice.count / xfmrByPhase.total) * 100 : 0}%;">
									{slice.count.toLocaleString()} ({Math.round((slice.count / xfmrByPhase.total) * 100)}%)
								</div>
							{/each}
						</div>
						<div class="split-bar-legend">
							{#each xfmrByPhase.items as slice}
								<span class="split-bar-legend-item" style="width: {xfmrByPhase.total > 0 ? (slice.count / xfmrByPhase.total) * 100 : 0}%; min-width: 55px;">
									<span class="device-phase phase-{slice.phase.toLowerCase()}">{slice.phase}</span>
								</span>
							{/each}
						</div>
					</div>
				{:else}
					<div class="section section-loading">
						<h5 class="section-title">Transformers by Phase</h5>
						<div class="section-spinner-row"><div class="spinner-sm"></div></div>
					</div>
				{/if}

				<!-- Devices by Phase -->
				{#if devicesByPhase?.items?.length > 0}
					<div class="section">
						<h5 class="section-title">Devices by Phase ({devicesByPhase.total})</h5>
						<div class="split-bar" style="height: 28px;">
							{#each devicesByPhase.items as slice}
								<div class="split-segment split-segment-label phase-seg-{slice.phase.toLowerCase()}" style="width: {devicesByPhase.total > 0 ? (slice.count / devicesByPhase.total) * 100 : 0}%;">
									{slice.count.toLocaleString()} ({Math.round((slice.count / devicesByPhase.total) * 100)}%)
								</div>
							{/each}
						</div>
						<div class="split-bar-legend">
							{#each devicesByPhase.items as slice}
								<span class="split-bar-legend-item" style="width: {devicesByPhase.total > 0 ? (slice.count / devicesByPhase.total) * 100 : 0}%; min-width: 55px;">
									<span class="device-phase phase-{slice.phase.toLowerCase()}">{slice.phase}</span>
								</span>
							{/each}
						</div>
					</div>
				{:else}
					<div class="section section-loading">
						<h5 class="section-title">Devices by Phase</h5>
						<div class="section-spinner-row"><div class="spinner-sm"></div></div>
					</div>
				{/if}

				<!-- Loads by Phase -->
				{#if loadsByPhase?.items?.length > 0}
					<div class="section">
						<h5 class="section-title">Loads by Phase ({loadsByPhase.total})</h5>
						<div class="split-bar" style="height: 28px;">
							{#each loadsByPhase.items as slice}
								<div class="split-segment split-segment-label phase-seg-{slice.phase.toLowerCase()}" style="width: {loadsByPhase.total > 0 ? (slice.count / loadsByPhase.total) * 100 : 0}%;">
									{slice.count.toLocaleString()} ({Math.round((slice.count / loadsByPhase.total) * 100)}%)
								</div>
							{/each}
						</div>
						<div class="split-bar-legend">
							{#each loadsByPhase.items as slice}
								<span class="split-bar-legend-item" style="width: {loadsByPhase.total > 0 ? (slice.count / loadsByPhase.total) * 100 : 0}%; min-width: 55px;">
									<span class="device-phase phase-{slice.phase.toLowerCase()}">{slice.phase}</span>
								</span>
							{/each}
						</div>
					</div>
				{:else}
					<div class="section section-loading">
						<h5 class="section-title">Loads by Phase</h5>
						<div class="section-spinner-row"><div class="spinner-sm"></div></div>
					</div>
				{/if}

			{/if}

			{#if activeTab === 'tables'}
				<!-- Lines by Table -->
				<div class="section">
					<h5 class="section-title">Lines</h5>
					{#if tableEdgeStats}
						<table class="table-count">
							{#each tableEdgeStats.tables as tc}
								<tr class="tc-row" class:tc-selected={selectedTable === 'edge-' + tc.table} onclick={() => selectedTable = selectedTable === 'edge-' + tc.table ? null : 'edge-' + tc.table}>
									<td class="tc-name">{tc.table}</td>
									<td class="tc-count">{tc.length.toFixed(1)} {tableEdgeStats.unit}</td>
								</tr>
								{#if selectedTable === 'edge-' + tc.table && tableEdgeStats.phaseBreakdown[tc.table]}
									{#each tableEdgeStats.phaseBreakdown[tc.table] as tp}
										<tr class="tc-phase-row">
											<td><span class="device-phase phase-{tp.phase.toLowerCase()}">{tp.phase}</span></td>
											<td class="tc-count">{tp.length.toFixed(1)} {tableEdgeStats.unit}</td>
										</tr>
									{/each}
								{/if}
							{/each}
						</table>
					{:else}
						<div class="section-spinner-row"><div class="spinner-sm"></div></div>
					{/if}
				</div>

				<!-- Devices by Table -->
				<div class="section">
					<h5 class="section-title">Devices</h5>
					{#if tableNodeStats}
						<table class="table-count">
							{#each tableNodeStats.tables as tc}
								<tr class="tc-row" class:tc-selected={selectedTable === 'node-' + tc.table} onclick={() => selectedTable = selectedTable === 'node-' + tc.table ? null : 'node-' + tc.table}>
									<td class="tc-name">{tc.table}</td>
									<td class="tc-count">{tc.count.toLocaleString()}</td>
								</tr>
								{#if selectedTable === 'node-' + tc.table && tableNodeStats.phaseBreakdown[tc.table]}
									{#each tableNodeStats.phaseBreakdown[tc.table] as tp}
										<tr class="tc-phase-row">
											<td><span class="device-phase phase-{tp.phase.toLowerCase()}">{tp.phase}</span></td>
											<td class="tc-count">{tp.count.toLocaleString()}</td>
										</tr>
									{/each}
								{/if}
							{/each}
						</table>
					{:else}
						<div class="section-spinner-row"><div class="spinner-sm"></div></div>
					{/if}
				</div>
			{/if}

			{#if activeTab === 'feeders'}
			{#if loadingFeeders}
				<div class="loading-container">
					<div class="spinner"></div>
					<p class="loading-text">Loading feeders...</p>
				</div>
			{:else if selectedFeeder}
				<!-- Feeder Detail View -->
				<div class="feeder-detail-header">
					<button class="back-btn" onclick={() => { selectedFeeder = null; clearFeederDetail(); }}>
						<Fa icon={faArrowLeft} size="0.9x" /> Back
					</button>
					<span class="feeder-detail-name">{selectedFeeder}</span>
				</div>

				<!-- Phase Breakdown -->
				<div class="section">
					<h5 class="section-title">Phase Breakdown</h5>
					{#if fdPhaseBreakdown}
						{#each fdPhaseBreakdown.phases as ph}
							<div class="phase-split-row">
								<span class="device-phase phase-{ph.phase.toLowerCase()}">{ph.phase}</span>
								<div class="phase-split-bar-area">
									<div class="split-bar phase-split-bar">
										<div class="split-segment split-primary" style="width: {ph.primaryPct}%"></div>
										<div class="split-segment split-secondary" style="width: {ph.secondaryPct}%"></div>
										<span class="split-bar-total">{ph.total} {fdPhaseBreakdown.unit}</span>
									</div>
									<div class="phase-split-values">
										<span class="phase-split-val"><span class="split-label-primary">Primary</span><span class="split-dot-inline split-dot-primary"></span>{ph.primary} {fdPhaseBreakdown.unit} ({ph.primaryPct}%)</span>
										<span class="phase-split-val"><span class="split-label-secondary">Secondary</span><span class="split-dot-inline split-dot-secondary"></span>{ph.secondary} {fdPhaseBreakdown.unit} ({ph.secondaryPct}%)</span>
									</div>
								</div>
							</div>
						{/each}
					{:else}
						<div class="section-spinner-row"><div class="spinner-sm"></div></div>
					{/if}
				</div>

				<!-- Transformer Balance -->
				<div class="section">
					<h5 class="section-title">Transformer Balance</h5>
					{#if fdXfmrBalance}
						{#if fdXfmrBalance.totalVabc > 0}
							<div class="balance-bar">
								<div class="balance-segment seg-a" style="width: {fdXfmrBalance.vaPct}%"></div>
								<div class="balance-segment seg-b" style="width: {fdXfmrBalance.vbPct}%"></div>
								<div class="balance-segment seg-c" style="width: {fdXfmrBalance.vcPct}%"></div>
							</div>
							<div class="balance-legend">
								<span class="balance-item"><span class="balance-dot seg-a-bg"></span>A: {fdXfmrBalance.vaMva} MVA ({fdXfmrBalance.vaPct}%)</span>
								<span class="balance-item"><span class="balance-dot seg-b-bg"></span>B: {fdXfmrBalance.vbMva} MVA ({fdXfmrBalance.vbPct}%)</span>
								<span class="balance-item"><span class="balance-dot seg-c-bg"></span>C: {fdXfmrBalance.vcMva} MVA ({fdXfmrBalance.vcPct}%)</span>
							</div>
						{:else}
							<p class="empty-text">No transformer balance data</p>
						{/if}
					{:else}
						<div class="section-spinner-row"><div class="spinner-sm"></div></div>
					{/if}
				</div>

				<!-- Transformers by Phase -->
				<div class="section">
					<h5 class="section-title">Transformers by Phase{#if fdXfmrByPhase} ({fdXfmrByPhase.total}){/if}</h5>
					{#if fdXfmrByPhase}
						{#if fdXfmrByPhase.items.length > 0}
							<div class="split-bar" style="height: 28px;">
								{#each fdXfmrByPhase.items as slice}
									<div class="split-segment split-segment-label phase-seg-{slice.phase.toLowerCase()}" style="width: {fdXfmrByPhase.total > 0 ? (slice.count / fdXfmrByPhase.total) * 100 : 0}%;">
										{slice.count.toLocaleString()} ({Math.round((slice.count / fdXfmrByPhase.total) * 100)}%)
									</div>
								{/each}
							</div>
							<div class="split-bar-legend">
								{#each fdXfmrByPhase.items as slice}
									<span class="split-bar-legend-item" style="width: {fdXfmrByPhase.total > 0 ? (slice.count / fdXfmrByPhase.total) * 100 : 0}%; min-width: 55px;">
										<span class="device-phase phase-{slice.phase.toLowerCase()}">{slice.phase}</span>
									</span>
								{/each}
							</div>
						{/if}
					{:else}
						<div class="section-spinner-row"><div class="spinner-sm"></div></div>
					{/if}
				</div>

				<!-- Devices by Phase -->
				<div class="section">
					<h5 class="section-title">Devices by Phase{#if fdDevicesByPhase} ({fdDevicesByPhase.total}){/if}</h5>
					{#if fdDevicesByPhase}
						{#if fdDevicesByPhase.items.length > 0}
							<div class="split-bar" style="height: 28px;">
								{#each fdDevicesByPhase.items as slice}
									<div class="split-segment split-segment-label phase-seg-{slice.phase.toLowerCase()}" style="width: {fdDevicesByPhase.total > 0 ? (slice.count / fdDevicesByPhase.total) * 100 : 0}%;">
										{slice.count.toLocaleString()} ({Math.round((slice.count / fdDevicesByPhase.total) * 100)}%)
									</div>
								{/each}
							</div>
							<div class="split-bar-legend">
								{#each fdDevicesByPhase.items as slice}
									<span class="split-bar-legend-item" style="width: {fdDevicesByPhase.total > 0 ? (slice.count / fdDevicesByPhase.total) * 100 : 0}%; min-width: 55px;">
										<span class="device-phase phase-{slice.phase.toLowerCase()}">{slice.phase}</span>
									</span>
								{/each}
							</div>
						{/if}
					{:else}
						<div class="section-spinner-row"><div class="spinner-sm"></div></div>
					{/if}
				</div>

				<!-- Loads by Phase -->
				<div class="section">
					<h5 class="section-title">Loads by Phase{#if fdLoadsByPhase} ({fdLoadsByPhase.total}){/if}</h5>
					{#if fdLoadsByPhase}
						{#if fdLoadsByPhase.items.length > 0}
							<div class="split-bar" style="height: 28px;">
								{#each fdLoadsByPhase.items as slice}
									<div class="split-segment split-segment-label phase-seg-{slice.phase.toLowerCase()}" style="width: {fdLoadsByPhase.total > 0 ? (slice.count / fdLoadsByPhase.total) * 100 : 0}%;">
										{slice.count.toLocaleString()} ({Math.round((slice.count / fdLoadsByPhase.total) * 100)}%)
									</div>
								{/each}
							</div>
							<div class="split-bar-legend">
								{#each fdLoadsByPhase.items as slice}
									<span class="split-bar-legend-item" style="width: {fdLoadsByPhase.total > 0 ? (slice.count / fdLoadsByPhase.total) * 100 : 0}%; min-width: 55px;">
										<span class="device-phase phase-{slice.phase.toLowerCase()}">{slice.phase}</span>
									</span>
								{/each}
							</div>
						{/if}
					{:else}
						<div class="section-spinner-row"><div class="spinner-sm"></div></div>
					{/if}
				</div>
			{:else}
				<!-- Feeder List View -->
				<div class="section">
					{#if feederList.length > 0}
						<div class="feeder-toolbar">
							<input
								class="input is-small feeder-filter"
								type="search"
								placeholder="Filter feeders..."
								bind:value={feederFilter}
							/>
							<div class="sort-buttons">
								<button class="sort-btn" class:sort-active={feederSort === 'length-desc'} title="Sort by length (high to low)" onclick={() => feederSort = 'length-desc'}><Fa icon={faSortNumericDown} /></button>
								<button class="sort-btn" class:sort-active={feederSort === 'length-asc'} title="Sort by length (low to high)" onclick={() => feederSort = 'length-asc'}><Fa icon={faSortNumericUp} /></button>
								<button class="sort-btn" class:sort-active={feederSort === 'alpha-asc'} title="Sort A–Z" onclick={() => feederSort = 'alpha-asc'}><Fa icon={faSortAlphaDown} /></button>
								<button class="sort-btn" class:sort-active={feederSort === 'alpha-desc'} title="Sort Z–A" onclick={() => feederSort = 'alpha-desc'}><Fa icon={faSortAlphaUp} /></button>
							</div>
						</div>
						<div class="feeder-list">
							{#each feederList.filter(f => {
								if (!feederFilter) return true;
								return f.name.toLowerCase().includes(feederFilter.toLowerCase());
							}).sort((a, b) => {
								if (feederSort === 'length-desc') return b.total - a.total;
								if (feederSort === 'length-asc') return a.total - b.total;
								if (feederSort === 'alpha-asc') return a.name.localeCompare(b.name);
								if (feederSort === 'alpha-desc') return b.name.localeCompare(a.name);
								return 0;
							}) as feeder}
								<div class="feeder-row" onclick={() => { selectedFeeder = feeder.name; loadFeederDetail(feeder.name); }}>
									<span class="feeder-name">{feeder.name}</span>
									<div class="feeder-bar-area">
										<div class="split-bar phase-split-bar">
											<div class="split-segment split-primary" style="width: {feeder.primaryPct}%"></div>
											<div class="split-segment split-secondary" style="width: {feeder.secondaryPct}%"></div>
											<span class="split-bar-total">{feeder.totalDisplay} {feederList.unit}</span>
										</div>
										<div class="phase-split-values">
											<span class="phase-split-val"><span class="split-label-primary">Primary</span><span class="split-dot-inline split-dot-primary"></span>{feeder.primaryDisplay} {feederList.unit} ({feeder.primaryPct}%)</span>
											<span class="phase-split-val"><span class="split-label-secondary">Secondary</span><span class="split-dot-inline split-dot-secondary"></span>{feeder.secondaryDisplay} {feederList.unit} ({feeder.secondaryPct}%)</span>
										</div>
									</div>
								</div>
							{/each}
						</div>
					{:else}
						<p class="empty-text">No feeder data available</p>
					{/if}
				</div>
			{/if}
			{/if}

			{#if activeTab === 'tie-devices'}
				<div class="section">
					{#if loadingTieDevices}
						<div class="loading-container">
							<div class="spinner"></div>
							<p class="loading-text">Loading tie devices...</p>
						</div>
					{:else if tieDevices && tieDevices.devices && tieDevices.devices.length > 0}
						<input
							class="input is-small tie-filter"
							type="search"
							placeholder="Filter by feeder, table, or ID..."
							bind:value={tieFilter}
						/>
						<div class="tie-device-list">
							{#each tieDevices.devices.filter(d => {
								if (!tieFilter) return true;
								const q = tieFilter.toLowerCase();
								return (d.feeder_a || '').toLowerCase().includes(q)
									|| (d.feeder_b || '').toLowerCase().includes(q)
									|| (d.lw_table || '').toLowerCase().includes(q)
									|| (d.lw_table_pkid || '').toLowerCase().includes(q);
							}) as device, i}
								<div
									class="tie-device-row"
									onclick={() => {
										if (device.geom) {
											zzzz.emit("goToTieDevice", device.geom);
										}
									}}
									onmouseenter={() => {
										if (device.geom) {
											zzzz.emit("highlightTieDevice", device.lw_table_pkid);
										}
									}}
									onmouseleave={() => {
										zzzz.emit("unhighlightTieDevice");
									}}
								>
									<div class="tie-device-feeders">
										{device.feeder_a} &harr; {device.feeder_b}
									</div>
									<div class="tie-device-sublabel">
										{device.lw_table} <span class="tie-device-pkid">#{device.lw_table_pkid}</span>
									</div>
								</div>
							{/each}
						</div>
						<div class="section-total">
							Total: <strong>{tieDevices.devices.length} tie devices</strong>
						</div>
					{:else}
						<p class="empty-text">No tie devices found</p>
					{/if}
				</div>
			{/if}
		</div>
</div>

<style>
	#network-info {
		width: 100%;
		padding: 8.775px;
	}

	/* Panel Header */
	.panel-header {
		display: flex;
		align-items: center;
		min-height: 45px;
		gap: 0.5rem;
	}

	.close-btn {
		background: none;
		border: none;
		cursor: pointer;
		color: #ff3e00;
		padding: 0;
	}

	.close-btn:hover {
		opacity: 0.7;
	}

	.panel-title {
		font-weight: bolder;
		padding-top: 2px;
	}

	/* Pinned top (summary + tabs) */
	.pinned-top {
		position: sticky;
		top: 0;
		z-index: 5;
		background-color: #ffffffee;
		backdrop-filter: blur(20px);
		padding-bottom: 0.25rem;
	}

	/* Summary Box */
	.summary-box {
		display: flex;
		gap: 1rem;
		padding: 1rem;
		background: rgba(0, 0, 0, 0.03);
		border-radius: 8px;
		border: 1px solid rgba(0, 0, 0, 0.08);
		margin-bottom: 0.75rem;
	}

	.feeder-count-box {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 0.25rem 1rem;
		border-right: 1px solid rgba(0, 0, 0, 0.1);
		min-width: 70px;
	}

	.big-number {
		font-size: 2rem;
		font-weight: 700;
		color: #667eea;
		line-height: 1;
	}

	.big-label {
		font-size: 0.7rem;
		color: #718096;
		text-transform: uppercase;
		letter-spacing: 0.05em;
	}

	.summary-stats {
		flex: 1;
		display: flex;
		flex-direction: column;
		gap: 0.4rem;
	}

	.stat-row {
		display: flex;
		justify-content: space-between;
		flex-wrap: wrap;
	}

	.stat {
		display: flex;
		flex-direction: column;
		align-items: center;
		flex: 1;
	}

	.stat-heading {
		font-size: 0.6rem;
		color: #a0aec0;
		text-transform: uppercase;
		letter-spacing: 0.03em;
	}

	.stat-value {
		font-size: 1rem;
		font-weight: 600;
		color: #2d3748;
	}

	.stat-value.color-primary {
		color: #48c9b0;
	}

	.stat-value.color-secondary {
		color: #d4a017;
	}

	.stat-pct {
		font-size: 0.65rem;
		font-weight: 500;
		color: #718096;
	}

	.stat-divider {
		height: 1px;
		background: rgba(0, 0, 0, 0.08);
	}

	/* Tabs */
	.tab-bar {
		display: flex;
		border-bottom: 2px solid #edf2f7;
	}

	.tab {
		flex: 1;
		padding: 0.6rem;
		background: none;
		border: none;
		cursor: pointer;
		font-size: 0.8rem;
		font-weight: 500;
		color: #718096;
		border-bottom: 2px solid transparent;
		margin-bottom: -2px;
		transition: all 0.2s;
	}

	.tab:hover {
		color: #4a5568;
	}

	.tab.active {
		color: #667eea;
		border-bottom-color: #667eea;
	}

	.tab-content {
		padding-bottom: 1rem;
	}

	/* Sections */
	.section {
		margin-bottom: 1rem;
		padding: 0.75rem;
		background: rgba(255, 255, 255, 0.05);
		border-radius: 8px;
		border: 1px solid rgba(0, 0, 0, 0.06);
	}

	.section-title {
		margin: 0 0 0.75rem 0;
		font-size: 0.85rem;
		font-weight: 600;
		color: #4a5568;
	}

	.section-total {
		margin-top: 0.75rem;
		padding-top: 0.5rem;
		border-top: 1px solid rgba(0, 0, 0, 0.06);
		font-size: 0.8rem;
		color: #718096;
	}

	.bar-track {
		flex: 1;
		height: 18px;
		background: rgba(0, 0, 0, 0.04);
		border-radius: 3px;
		overflow: hidden;
	}

	.bar-fill {
		height: 100%;
		border-radius: 3px;
		transition: width 0.6s ease-out;
	}

	/* Primary / Secondary Split */
	.split-bar {
		display: flex;
		position: relative;
		height: 22px;
		border-radius: 4px;
		overflow: hidden;
		background: rgba(0, 0, 0, 0.04);
	}

	.split-segment {
		height: 100%;
		transition: width 0.6s ease-out;
	}

	.split-segment-label {
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 0.6rem;
		font-weight: 600;
		color: #2d3748;
		overflow: hidden;
		white-space: nowrap;
		min-width: 55px;
		border-radius: 4px;
		border: 1px solid rgba(0, 0, 0, 0.12);
		margin: 0 1px;
	}

	.split-primary { background: #48c9b0; }
	.split-secondary { background: #f0c040; }
	.split-device { background: #667eea; }
	.split-transformer { background: #e67e22; }
	.split-load { background: #9b59b6; }

	.split-bar-total {
		position: absolute;
		left: 50%;
		top: 50%;
		transform: translate(-50%, -50%);
		font-size: 0.65rem;
		font-weight: 600;
		color: #2d3748;
		pointer-events: none;
	}

	/* Per-Phase Split */
	.phase-split-row {
		display: flex;
		align-items: center;
		gap: 0.6rem;
		margin-bottom: 0.6rem;
	}

	.phase-split-row:last-child {
		margin-bottom: 0;
	}

	.device-phase {
		border-radius: 4px;
		padding: 0.1rem 0.5rem;
		font-size: 0.75rem;
		font-weight: 500;
		width: 40px;
		text-align: center;
		display: inline-block;
		flex-shrink: 0;
	}

	.split-bar-legend {
		display: flex;
		margin-top: 0.25rem;
	}

	.split-bar-legend-item {
		display: flex;
		justify-content: center;
		margin: 0 1px;
	}

	/* Phase segment fills (faded, matching device-phase style) */
	.phase-seg-a { background-color: rgba(152, 32, 19, 0.2); }
	.phase-seg-b { background-color: rgba(25, 149, 12, 0.2); }
	.phase-seg-c { background-color: rgba(24, 51, 139, 0.2); }
	.phase-seg-ab { background: linear-gradient(135deg, rgba(152, 32, 19, 0.2) 50%, rgba(25, 149, 12, 0.2) 50%); }
	.phase-seg-ac { background: linear-gradient(135deg, rgba(152, 32, 19, 0.2) 50%, rgba(24, 51, 139, 0.2) 50%); }
	.phase-seg-bc { background: linear-gradient(135deg, rgba(25, 149, 12, 0.2) 50%, rgba(24, 51, 139, 0.2) 50%); }
	.phase-seg-abc { background: linear-gradient(135deg, rgba(152, 32, 19, 0.2) 33%, rgba(25, 149, 12, 0.2) 33%, rgba(25, 149, 12, 0.2) 66%, rgba(24, 51, 139, 0.2) 66%); }

	/* Phase label chips (light bg) */
	.phase-a { background-color: rgba(152, 32, 19, 0.2); color: #982013; }
	.phase-b { background-color: rgba(25, 149, 12, 0.2); color: #19950c; }
	.phase-c { background-color: rgba(24, 51, 139, 0.2); color: #18338b; }
	.phase-ab { background: linear-gradient(135deg, rgba(152, 32, 19, 0.2) 50%, rgba(25, 149, 12, 0.2) 50%); color: #333; }
	.phase-ac { background: linear-gradient(135deg, rgba(152, 32, 19, 0.2) 50%, rgba(24, 51, 139, 0.2) 50%); color: #333; }
	.phase-bc { background: linear-gradient(135deg, rgba(25, 149, 12, 0.2) 50%, rgba(24, 51, 139, 0.2) 50%); color: #333; }
	.phase-abc { background: linear-gradient(135deg, rgba(152, 32, 19, 0.2) 33%, rgba(25, 149, 12, 0.2) 33%, rgba(25, 149, 12, 0.2) 66%, rgba(24, 51, 139, 0.2) 66%); color: #333; }

	.phase-split-bar-area {
		flex: 1;
		display: flex;
		flex-direction: column;
		gap: 0.2rem;
	}

	.phase-split-bar {
		height: 14px;
	}

	.phase-split-values {
		display: flex;
		justify-content: space-between;
		font-size: 0.65rem;
		color: #718096;
	}

	.phase-split-val {
		display: flex;
		align-items: center;
		gap: 0.25rem;
	}

	.split-dot-inline {
		width: 6px;
		height: 6px;
		border-radius: 1px;
		display: inline-block;
		flex-shrink: 0;
	}

	.split-dot-primary { background: #48c9b0; }
	.split-dot-secondary { background: #f0c040; }
	.split-label-primary { color: #48c9b0; font-size: 0.65rem; font-weight: 600; margin-right: 2px; }
	.split-label-secondary { color: #f0c040; font-size: 0.65rem; font-weight: 600; margin-right: 2px; }
	.split-dot-device { background: #667eea; }
	.split-dot-transformer { background: #e67e22; }
	.split-dot-load { background: #9b59b6; }

	/* Transformer Balance */
	.balance-bar {
		display: flex;
		height: 28px;
		border-radius: 4px;
		overflow: hidden;
	}

	.balance-segment {
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 0.7rem;
		font-weight: 600;
		color: white;
		transition: opacity 0.2s;
	}

	.balance-segment:hover {
		opacity: 0.85;
	}

	.seg-a { background: rgb(152, 32, 19); }
	.seg-b { background: rgb(25, 149, 12); }
	.seg-c { background: rgb(24, 51, 139); }

	.balance-legend {
		display: flex;
		justify-content: space-around;
		margin-top: 0.5rem;
		font-size: 0.75rem;
		color: #4a5568;
	}

	.balance-item {
		display: flex;
		align-items: center;
		gap: 0.3rem;
	}

	.balance-dot {
		width: 8px;
		height: 8px;
		border-radius: 2px;
		display: inline-block;
	}

	.seg-a-bg { background: rgb(152, 32, 19); }
	.seg-b-bg { background: rgb(25, 149, 12); }
	.seg-c-bg { background: rgb(24, 51, 139); }

	/* Vertical Bar Chart */
	.vbar-chart {
		display: flex;
		align-items: flex-end;
		gap: 0.4rem;
		height: 160px;
		padding-top: 1.2rem;
	}

	.vbar-column {
		flex: 1;
		display: flex;
		flex-direction: column;
		align-items: center;
		height: 100%;
	}

	.vbar-count {
		font-size: 0.65rem;
		font-weight: 600;
		color: #4a5568;
		margin-bottom: 0.2rem;
	}

	.vbar-track {
		flex: 1;
		width: 100%;
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
		border-radius: 3px 3px 0 0;
		overflow: hidden;
	}

	.vbar-fill {
		width: 100%;
		border-radius: 3px 3px 0 0;
		transition: height 0.6s ease-out;
	}

	/* Table Count */
	.table-count {
		width: 100%;
		border-collapse: collapse;
	}

	.table-count tr {
		border-bottom: 1px solid rgba(0, 0, 0, 0.06);
	}

	.table-count tr:last-child {
		border-bottom: none;
	}

	.tc-name {
		font-size: 0.75rem;
		color: #4a5568;
		padding: 0.3rem 0;
	}

	.tc-count {
		font-size: 0.75rem;
		font-weight: 600;
		color: #2d3748;
		text-align: right;
		padding: 0.3rem 0;
	}

	.tc-header td {
		font-size: 0.7rem;
		font-weight: 700;
		color: #718096;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		padding: 0.5rem 0 0.2rem;
	}

	.tc-row {
		cursor: pointer;
	}

	.tc-row:hover {
		background: rgba(0, 0, 0, 0.03);
	}

	.tc-selected {
		background: rgba(102, 126, 234, 0.08);
	}

	.tc-phase-row {
		background: rgba(0, 0, 0, 0.02);
	}

	.tc-phase-row td {
		padding: 0.2rem 0 0.2rem 1rem;
		font-size: 0.7rem;
	}

	/* Device Type Breakdown */
	.device-type-label {
		min-width: 120px;
		font-size: 0.75rem;
		font-weight: 500;
		color: #4a5568;
	}

	.device-type-bar {
		background: #667eea;
	}

	/* Feeder List */
	.feeder-list {
		display: flex;
		flex-direction: column;
	}

	.feeder-detail-header {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		padding: 0.5rem 0.75rem;
	}

	.back-btn {
		background: none;
		border: 1px solid rgba(0, 0, 0, 0.12);
		border-radius: 4px;
		cursor: pointer;
		padding: 0.3rem 0.6rem;
		font-size: 0.75rem;
		font-weight: 500;
		color: #667eea;
		display: flex;
		align-items: center;
		gap: 0.3rem;
		transition: all 0.15s;
	}

	.back-btn:hover {
		background: rgba(102, 126, 234, 0.08);
		border-color: #667eea;
	}

	.feeder-detail-name {
		font-size: 0.95rem;
		font-weight: 600;
		color: #2d3748;
	}

	.feeder-row {
		display: flex;
		flex-direction: column;
		gap: 0.2rem;
		padding: 0.5rem 0.25rem;
		border-bottom: 1px solid #edf2f7;
		cursor: pointer;
	}

	.feeder-row:hover {
		background: rgba(102, 126, 234, 0.04);
	}

	.feeder-row:last-child {
		border-bottom: none;
	}

	.feeder-info {
		display: flex;
		flex-direction: column;
		min-width: 120px;
	}

	.feeder-name {
		font-size: 0.8rem;
		font-weight: 500;
		color: #2d3748;
	}

	.feeder-sub {
		font-size: 0.65rem;
		color: #a0aec0;
	}

	.feeder-bar-area {
		flex: 1;
		display: flex;
		flex-direction: column;
		gap: 0.2rem;
	}

	.feeder-bar {
		background: #667eea;
	}

	.feeder-total-label {
		font-size: 0.7rem;
		font-weight: 600;
		color: #718096;
	}

	/* Tie Devices */
	.feeder-toolbar {
		display: flex;
		gap: 0.5rem;
		margin-bottom: 0.75rem;
		align-items: center;
	}

	.feeder-filter {
		flex: 1;
	}

	.sort-buttons {
		display: flex;
		gap: 2px;
	}

	.sort-btn {
		background: none;
		border: 1px solid rgba(0, 0, 0, 0.12);
		border-radius: 4px;
		cursor: pointer;
		padding: 0.3rem 0.45rem;
		color: #a0aec0;
		transition: all 0.15s;
	}

	.sort-btn:hover {
		color: #4a5568;
		border-color: rgba(0, 0, 0, 0.25);
	}

	.sort-active {
		color: #667eea;
		border-color: #667eea;
		background: rgba(102, 126, 234, 0.08);
	}

	.tie-filter {
		margin-bottom: 0.75rem;
	}

	.tie-device-list {
		display: flex;
		flex-direction: column;
		gap: 0.25rem;
	}

	.tie-device-row {
		cursor: pointer;
		transition: all 0.2s ease;
		padding: 0.4rem 0.5rem;
		border-radius: 4px;
		border-bottom: 1px solid #eee;
	}

	.tie-device-row:hover {
		background-color: rgba(230, 126, 34, 0.1);
	}

	.tie-device-row:last-child {
		border-bottom: none;
	}

	.tie-device-feeders {
		font-size: 0.85rem;
		font-weight: 500;
		color: #e67e22;
	}

	.tie-device-sublabel {
		font-size: 0.7rem;
		color: #718096;
	}

	.tie-device-pkid {
		opacity: 0.7;
	}

	.empty-text {
		text-align: center;
		color: #a0aec0;
		font-size: 0.85rem;
		padding: 1.5rem 0;
	}

	/* Loading */
	.loading-container {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 2rem;
		gap: 1rem;
	}

	.spinner {
		width: 40px;
		height: 40px;
		border: 3px solid rgba(102, 126, 234, 0.1);
		border-top: 3px solid #667eea;
		border-radius: 50%;
		animation: spin 1s linear infinite;
	}

	.loading-text {
		color: #667eea;
		font-size: 0.9rem;
		font-weight: 500;
		margin: 0;
	}

	@keyframes spin {
		0% { transform: rotate(0deg); }
		100% { transform: rotate(360deg); }
	}

	/* Section-level loading spinner */
	.section-loading {
		opacity: 0.6;
	}

	.section-spinner-row {
		display: flex;
		justify-content: center;
		padding: 0.75rem 0;
	}

	.spinner-sm {
		width: 22px;
		height: 22px;
		border: 2px solid rgba(102, 126, 234, 0.15);
		border-top: 2px solid #667eea;
		border-radius: 50%;
		animation: spin 1s linear infinite;
	}
</style>
